// SPDX-License-Identifier: MIT
// /facons — l'échelle BYOAI : Web → Terminal → App desktop (progression).
import { t } from '../i18n/t';
import { escapeHtml } from '../lib/escape';
import { renderHeader } from '../components/header';
import { renderFooter } from '../components/footer';
import type { Locale } from '../i18n/detect';

export interface FaconsPageOptions {
  locale?: Locale;
  // Authed chrome (best-effort) — ✦ idées pill + account menu when signed in.
  user?: { email: string };
  onSignOut?: () => void | Promise<void>;
}

// Public beta download lives on our own /download page (binaries served from the
// VPS — the Forgejo instance requires sign-in, so it can't host anonymous public
// downloads). Internal SPA route, not an external link.
const DESKTOP_BETA_URL = '/download';

export function renderFaconsPage(opts: FaconsPageOptions = {}): DocumentFragment {
  const frag = document.createDocumentFragment();
  frag.appendChild(renderHeader({ locale: opts.locale, activePath: '/facons', currentRung: 'web', user: opts.user, onSignOut: opts.onSignOut }));

  const main = document.createElement('main');
  main.className = 'facons';
  main.setAttribute('data-component', 'facons-page');

  // Each rung carries a footnote under its CTA: a colored tag + 3 short label→fact
  // lines. They are deliberately EQUAL height (tag + 3 single lines) so the three
  // CTAs align across the row (the desktop note used to push its button up). The
  // desktop footnote doubles as the unsigned-beta contournement guide (the OS
  // shows a one-time warning on first launch — this says how to get past it).
  const rungNote = (mod: 'web' | 'terminal' | 'desktop', tag: string, items: Array<[string, string]>) => `
      <div class="facons-rung__note facons-rung__note--${mod}" data-component="facons-rung-note">
        <span class="facons-rung__note-tag">${escapeHtml(tag)}</span>
        <ul class="facons-rung__note-list">
          ${items.map(([k, v]) => `<li><b class="facons-rung__note-k">${escapeHtml(k)}</b><span>${escapeHtml(v)}</span></li>`).join('')}
        </ul>
      </div>`;
  const noteItems = (id: 'web' | 'terminal', keys: string[]) =>
    keys.map((k) => [t(`facons.${id}.note.${k}`), t(`facons.${id}.note.${k}v`)] as [string, string]);
  const webNote = () => rungNote('web', t('facons.web.note.tag'), noteItems('web', ['k1', 'k2', 'k3']));
  const terminalNote = () => rungNote('terminal', t('facons.terminal.note.tag'), noteItems('terminal', ['k1', 'k2', 'k3']));
  // OS names are language-neutral (kept in markup); only the gesture is translated.
  const desktopNote = () => rungNote('desktop', t('facons.desktop.betaTag'), [
    ['macOS', t('facons.desktop.notice.mac')],
    ['Windows', t('facons.desktop.notice.win')],
    ['Linux', t('facons.desktop.notice.linux')],
  ]);

  const rung = (
    id: 'web' | 'terminal' | 'desktop',
    href: string,
    dataNav: boolean,
    _step: string,
    extraHtml = '',
    external = false,
  ) => `
    <article class="facons-rung facons-rung--${id}">
      <div class="facons-rung__name">${escapeHtml(t(`facons.${id}.name`))}</div>
      <div class="facons-rung__quality">→ ${escapeHtml(t(`facons.${id}.quality`))}</div>
      <div class="facons-rung__cost">${escapeHtml(t(`facons.${id}.cost`))}</div>
      <p class="facons-rung__pitch">${escapeHtml(t(`facons.${id}.pitch`))}</p>
      <a class="facons-rung__cta facons-rung__cta--${id}"${dataNav ? ' data-nav' : ''}${external ? ' target="_blank" rel="noopener"' : ''} href="${href}">${escapeHtml(t(`facons.${id}.cta`))}</a>
      ${extraHtml}
    </article>`;

  main.innerHTML = `
    <h1 class="facons__title">${escapeHtml(t('facons.pageTitle'))}</h1>
    <p class="facons__lead">${escapeHtml(t('facons.lead'))}</p>
    <p class="facons__byoai">// ${escapeHtml(t('facons.byoai'))}</p>
    <section class="facons__ladder">
      ${rung('web', '/facons', true, '①', webNote())}
      ${rung('terminal', '/connecter', true, '②', terminalNote())}
      ${/* Desktop beta IS published now (Linux live; Mac/Win billing-gated on
           GitHub). CTA → internal /download page + per-OS "get past the unsigned
           warning" note. */ ''}
      ${rung('desktop', DESKTOP_BETA_URL, true, '③', desktopNote())}
    </section>
    <div class="facons__cloud">
      <span>${escapeHtml(t('facons.cloudNote'))}</span>
      <a data-nav href="/tarifs">${escapeHtml(t('facons.cloudCta'))}</a>
    </div>
  `;
  frag.appendChild(main);
  frag.appendChild(renderFooter());
  return frag;
}
